import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Descriptions, Drawer, Empty,
  Form, Input, Modal, Row, Select, Space, Statistic, Table, Tabs, Tag,
  Tooltip, Typography, message
} from 'antd';
import {
  ReloadOutlined, PlusOutlined, EyeOutlined, DeleteOutlined,
  PlayCircleOutlined, FileDoneOutlined, ExclamationCircleOutlined,
  ProfileOutlined, RiseOutlined, FallOutlined, MinusCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { ErpMasterShell } from '../../../../components/common/erp-grid';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { yearEndClosingApi } from './yearEndClosingApi';
import {
  CLOSING_STATUS_COLOR, CLOSING_STATUS_LABEL,
  LINE_ROLE_LABEL,
  formatRupee, netProfitLabel, netProfitColor,
  isDifferenceWithinTolerance,
  canDeleteClosing, canPreviewClosing, canPostClosing,
  canCreateOpeningVoucher,
  buildClosingPayload, findClosingFormErrors,
  groupPreviewLines, friendlyYecErrorMessage
} from './yearEndClosingHelpers';
import {
  friendlyAccountsErrorMessage, friendlyAccountsEmptyMessage
} from '../accountsPageState';
import '../accounts.css';

const { Title, Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Year-End Closing page (Stage 6E.2).
//
// List + create modal + preview drawer + Post / Opening-Voucher /
// Delete actions. The closing JV always routes through the
// voucherPosting engine (server-side); the opening balance voucher
// is a metadata snapshot (see service header).
// ─────────────────────────────────────────────────────────────────

function RetainedEarningsSelect({ value, onChange, disabled, placeholder }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (q) => {
    setLoading(true);
    try {
      const out = await yearEndClosingApi.retainedEarningsLookup(q || '', 50);
      setOptions(out || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(''); }, [fetch]);

  return (
    <Select
      size="small"
      showSearch
      filterOption={false}
      loading={loading}
      disabled={disabled}
      value={value}
      onChange={onChange}
      onSearch={(t) => fetch(t)}
      placeholder={placeholder || 'Select retained earnings / capital ledger'}
      style={{ width: '100%' }}
      options={(options || []).map((o) => ({
        value: o.id,
        label: `${o.accountCode || ''} · ${o.accountName || ''}`.trim()
      }))}
    />
  );
}

function NewClosingModal({ open, onClose, onCreated }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    let values;
    try { values = await form.validateFields(); }
    catch (_e) { return; }
    const payload = buildClosingPayload({
      financialYearLabel: values.financialYearLabel,
      fromDate:    values.range?.[0]?.format('YYYY-MM-DD'),
      toDate:      values.range?.[1]?.format('YYYY-MM-DD'),
      closingDate: values.closingDate?.format('YYYY-MM-DD'),
      retainedEarningsAccountId: values.retainedEarningsAccountId,
      closeNotes:  values.closeNotes
    });
    const errs = findClosingFormErrors({
      financialYearLabel: payload.financialYearLabel,
      fromDate: payload.fromDate, toDate: payload.toDate,
      closingDate: payload.closingDate,
      retainedEarningsAccountId: payload.retainedEarningsAccountId
    });
    if (errs.length) { message.error(errs[0][1]); return; }
    setSaving(true);
    try {
      const created = await yearEndClosingApi.create(payload);
      message.success('Year-end closing draft created.');
      form.resetFields();
      onCreated && onCreated(created);
    } catch (e) {
      message.error(friendlyAccountsErrorMessage(e, 'year_end_closing'));
    } finally { setSaving(false); }
  }, [form, onCreated]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={save}
      okText="Create draft"
      confirmLoading={saving}
      title="New Year-End Closing"
      width={640}
      destroyOnClose>
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item label="Financial year label" name="financialYearLabel"
                    rules={[{ required: true, message: 'Financial year label is required.' }]}
                    extra="Example: 2025-26 or FY2025-26">
          <Input maxLength={20} />
        </Form.Item>
        <Form.Item label="Statement period" name="range"
                    rules={[{ required: true, message: 'Pick the FY period.' }]}>
          <DatePicker.RangePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item label="Closing date" name="closingDate"
                    rules={[{ required: true, message: 'Closing date is required.' }]}
                    extra="Usually the FY end date or a few days after (must be ≥ end date).">
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item label="Retained earnings / capital ledger"
                    name="retainedEarningsAccountId"
                    rules={[{ required: true,
                              message: 'Select an Equity ledger for retained earnings or capital transfer.' }]}>
          <RetainedEarningsSelect />
        </Form.Item>
        <Form.Item label="Notes (optional)" name="closeNotes">
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function PreviewDrawer({ open, closing, onClose, onRefresh, onPostDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lines, setLines]     = useState([]);
  const [obv, setObv]         = useState(null);
  const [posting, setPosting] = useState(false);
  const [obvBusy, setObvBusy] = useState(false);
  const { runWithApproval } = useApprovalMfa();

  const refresh = useCallback(async () => {
    if (!closing) return;
    setLoading(true);
    try {
      const linesOut = await yearEndClosingApi.lines(closing.id);
      setLines(linesOut?.rows || []);
    } catch (e) {
      message.error(friendlyYecErrorMessage(e));
    } finally { setLoading(false); }
  }, [closing]);

  useEffect(() => { if (open) { setData(null); refresh(); } }, [open, refresh]);

  const runPreview = useCallback(async () => {
    if (!closing) return;
    setLoading(true);
    try {
      const out = await yearEndClosingApi.preview(closing.id);
      setData(out);
      setLines((out?.lines?.incomeClose || [])
        .concat(out?.lines?.expenseClose || [])
        .concat(out?.lines?.retainedEarnings ? [out.lines.retainedEarnings] : [])
        .concat(out?.lines?.carryForward || []));
      message.success('Preview regenerated.');
      await onRefresh();
    } catch (e) {
      message.error(friendlyAccountsErrorMessage(e, 'year_end_closing'));
    } finally { setLoading(false); }
  }, [closing, onRefresh]);

  const doPost = useCallback(async () => {
    if (!closing) return;
    Modal.confirm({
      title: `Post closing for FY ${closing.financialYearLabel}?`,
      icon:  <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>This will post the closing voucher dated <code>{closing.toDate}</code>, transferring net profit/loss to the chosen retained earnings ledger.</p>
          <p style={{ marginBottom: 0 }}>The financial year periods will be locked. This action requires Approval-MFA.</p>
        </div>
      ),
      okText: 'Post closing',
      onOk: () => {
        setPosting(true);
        return runWithApproval({
          operationKey: 'accounts.year_end.post',
          resourceType: 'year_end_closing',
          resourceId:   closing.id,
          run: ({ challengeId }) =>
            yearEndClosingApi.post(closing.id, { approvalMfaChallengeId: challengeId })
        })
        .then(async (res) => {
          message.success(`Closing posted. Voucher ${res?.closingVoucher?.voucherNo || ''} created.`);
          await onPostDone();
          await refresh();
        })
        .catch((e) => message.error(friendlyAccountsErrorMessage(e, 'year_end_closing')))
        .finally(() => setPosting(false));
      }
    });
  }, [closing, runWithApproval, onPostDone, refresh]);

  const doOpeningVoucher = useCallback(async () => {
    if (!closing) return;
    setObvBusy(true);
    try {
      const res = await runWithApproval({
        operationKey: 'accounts.year_end.opening_voucher',
        resourceType: 'year_end_closing',
        resourceId:   closing.id,
        run: ({ challengeId }) =>
          yearEndClosingApi.createOpeningVoucher(closing.id, {},
            { approvalMfaChallengeId: challengeId })
      });
      setObv(res?.voucher || null);
      message.success('Opening balance snapshot recorded.');
    } catch (e) {
      message.error(friendlyAccountsErrorMessage(e, 'year_end_closing'));
    } finally { setObvBusy(false); }
  }, [closing, runWithApproval]);

  const grouped = useMemo(() => groupPreviewLines(lines), [lines]);

  if (!closing) return null;

  const summary = data?.summary || {
    totalIncome:               closing.totalIncome,
    totalExpense:              closing.totalExpense,
    netProfit:                 closing.netProfitLoss,
    carriedForwardAssets:      closing.carriedForwardAssets,
    carriedForwardLiabilities: closing.carriedForwardLiabilities,
    carriedForwardEquity:      closing.carriedForwardEquity,
    balanceSheetDifference:    closing.differenceAmount
  };
  const warnings = data?.warnings || (closing.previewJson?.warnings || []);
  const canPost = canPostClosing(closing.status, summary.balanceSheetDifference);
  const canOBV  = canCreateOpeningVoucher(closing.status, obv);

  const lineColumns = [
    { title: 'Code', dataIndex: 'ledgerAccountCode', width: 110,
      render: (v) => v ? <code style={{ fontSize: 11 }}>{v}</code>
                       : <Text type="secondary">—</Text> },
    { title: 'Name', dataIndex: 'ledgerAccountName', ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Type', dataIndex: 'accountType', width: 100,
      render: (v) => v ? <Tag>{v}</Tag> : null },
    { title: 'Closing DR', dataIndex: 'closingDebit',  align: 'right', width: 120,
      render: (v) => v > 0 ? formatRupee(v) : <Text type="secondary">—</Text> },
    { title: 'Closing CR', dataIndex: 'closingCredit', align: 'right', width: 120,
      render: (v) => v > 0 ? formatRupee(v) : <Text type="secondary">—</Text> },
    { title: 'Opening DR', dataIndex: 'openingDebit',  align: 'right', width: 120,
      render: (v) => v > 0 ? formatRupee(v) : <Text type="secondary">—</Text> },
    { title: 'Opening CR', dataIndex: 'openingCredit', align: 'right', width: 120,
      render: (v) => v > 0 ? formatRupee(v) : <Text type="secondary">—</Text> }
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={1100}
      destroyOnClose
      title={`Year-End Closing — ${closing.financialYearLabel}`}
      extra={
        <Space>
          <Tag color={CLOSING_STATUS_COLOR[closing.status]}>
            {CLOSING_STATUS_LABEL[closing.status]}
          </Tag>
        </Space>
      }>
      <Descriptions size="small" bordered column={3}>
        <Descriptions.Item label="Closing code">
          <code>{closing.closingCode}</code>
        </Descriptions.Item>
        <Descriptions.Item label="Period">
          {closing.fromDate} → {closing.toDate}
        </Descriptions.Item>
        <Descriptions.Item label="Closing date">{closing.closingDate}</Descriptions.Item>
        <Descriptions.Item label="Retained Earnings" span={3}>
          {closing.retainedEarningsAccountCode ? (
            <code style={{ marginRight: 6 }}>{closing.retainedEarningsAccountCode}</code>
          ) : null}
          {closing.retainedEarningsAccountName || '—'}
        </Descriptions.Item>
      </Descriptions>

      <Row gutter={[10, 10]} style={{ marginTop: 12 }}>
        <Col xs={12} md={5}>
          <Card size="small"><Statistic title="Total Income (₹)"
            value={summary.totalIncome  || 0} precision={2} /></Card>
        </Col>
        <Col xs={12} md={5}>
          <Card size="small"><Statistic title="Total Expense (₹)"
            value={summary.totalExpense || 0} precision={2}
            valueStyle={{ color: '#b65b00' }} /></Card>
        </Col>
        <Col xs={12} md={5}>
          <Card size="small">
            <Statistic title="Net Profit / Loss (₹)"
              value={Math.abs(Number(summary.netProfit) || 0)} precision={2}
              valueStyle={{ color: netProfitColor(summary.netProfit) }}
              prefix={
                (summary.netProfit > 0) ? <RiseOutlined />
                : (summary.netProfit < 0) ? <FallOutlined />
                : <MinusCircleOutlined />
              } />
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small"><Statistic title="Assets carried (₹)"
            value={summary.carriedForwardAssets || 0} precision={2} /></Card>
        </Col>
        <Col xs={12} md={5}>
          <Card size="small">
            <Statistic title="Difference (₹)"
              value={Math.abs(Number(summary.balanceSheetDifference) || 0)}
              precision={2}
              valueStyle={{ color: isDifferenceWithinTolerance(summary.balanceSheetDifference)
                ? '#1f7a25' : '#b91c1c' }} />
          </Card>
        </Col>
      </Row>

      {warnings && warnings.length ? (
        <Alert
          type="warning" showIcon style={{ marginTop: 10 }}
          message={`${warnings.length} warning(s) to review`}
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {warnings.map((w, i) => (
                <li key={i}>{w.message || w.code}</li>
              ))}
            </ul>
          } />
      ) : null}

      <Space style={{ marginTop: 12 }}>
        <Button size="small" type="primary" icon={<PlayCircleOutlined />}
          onClick={runPreview} loading={loading}
          disabled={!canPreviewClosing(closing.status)}>
          {closing.status === 'draft' ? 'Generate preview' : 'Regenerate preview'}
        </Button>
        <Button size="small" type="primary" danger={false} icon={<FileDoneOutlined />}
          loading={posting} disabled={!canPost} onClick={doPost}>
          Post closing
        </Button>
        <Button size="small" icon={<ProfileOutlined />}
          loading={obvBusy} disabled={!canOBV} onClick={doOpeningVoucher}>
          Record opening snapshot
        </Button>
      </Space>

      <Tabs style={{ marginTop: 10 }} items={[
        { key: 'income', label: `Income closed (${grouped.incomeClose.length})`,
          children: <Table size="small" rowKey="id"
            columns={lineColumns} dataSource={grouped.incomeClose}
            pagination={false}
            locale={{ emptyText: <Empty description="No income lines" /> }} /> },
        { key: 'expense', label: `Expense closed (${grouped.expenseClose.length})`,
          children: <Table size="small" rowKey="id"
            columns={lineColumns} dataSource={grouped.expenseClose}
            pagination={false}
            locale={{ emptyText: <Empty description="No expense lines" /> }} /> },
        { key: 're',     label: `Retained earnings (${grouped.retainedEarnings.length})`,
          children: <Table size="small" rowKey="id"
            columns={lineColumns} dataSource={grouped.retainedEarnings}
            pagination={false}
            locale={{ emptyText: <Empty description="No retained earnings line yet — generate preview." /> }} /> },
        { key: 'cf',     label: `Carry-forward (${grouped.carryForward.length})`,
          children: <Table size="small" rowKey="id"
            columns={lineColumns} dataSource={grouped.carryForward}
            pagination={{ pageSize: 25, size: 'small' }}
            locale={{ emptyText: <Empty description="No carry-forward balances." /> }} /> }
      ]} />

      <Alert
        type="info" showIcon style={{ marginTop: 12, fontSize: 12 }}
        message="Opening balance voucher: snapshot only"
        description="The opening balance voucher is recorded as a snapshot. Asset / liability / equity balances carry forward natively through the cumulative current_balance model — posting a true opening voucher would double-count them." />
    </Drawer>
  );
}

export default function YearEndClosingPage() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [active, setActive]   = useState(null);
  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  const fetchAll = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const out = await yearEndClosingApi.list(params);
      setRows(out?.rows || []);
    } catch (e) {
      setLoadError(e);
      message.error(friendlyAccountsErrorMessage(e, 'year_end_closing'));
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openDetail = useCallback(async (rec) => {
    try {
      const full = await yearEndClosingApi.get(rec.id);
      setActive(full);
      setDrawerOpen(true);
    } catch (e) {
      message.error(friendlyAccountsErrorMessage(e, 'year_end_closing'));
    }
  }, []);

  const onCreated = useCallback((created) => {
    setModalOpen(false);
    fetchAll();
    if (created && created.id) openDetail(created);
  }, [fetchAll, openDetail]);

  const refreshActive = useCallback(async () => {
    if (!active) return;
    try {
      const full = await yearEndClosingApi.get(active.id);
      setActive(full);
      await fetchAll();
    } catch (_e) { /* swallow */ }
  }, [active, fetchAll]);

  const onPostDone = useCallback(async () => {
    await refreshActive();
  }, [refreshActive]);

  const onRemove = useCallback((rec) => {
    Modal.confirm({
      title: `Delete year-end closing ${rec.closingCode}?`,
      content: 'Only draft / previewed / cancelled closings can be deleted. Closed and reopened closings cannot.',
      okText: 'Delete', okButtonProps: { danger: true },
      onOk: () => runWithApproval({
        operationKey: 'accounts.year_end.delete',
        resourceType: 'year_end_closing',
        resourceId:   rec.id,
        run: ({ challengeId }) =>
          yearEndClosingApi.remove(rec.id, { approvalMfaChallengeId: challengeId })
      })
      .then(() => { message.success('Closing deleted.'); return fetchAll(); })
      .catch((e) => message.error(friendlyAccountsErrorMessage(e, 'year_end_closing')))
    });
  }, [runWithApproval, fetchAll]);

  const columns = useMemo(() => ([
    { title: 'Code', dataIndex: 'closingCode', width: 140,
      render: (v) => <code style={{ fontSize: 12 }}>{v}</code> },
    { title: 'Financial Year', dataIndex: 'financialYearLabel', width: 130 },
    { title: 'Period', key: 'period', width: 220,
      render: (_, r) => `${r.fromDate} → ${r.toDate}` },
    { title: 'Net P/L', key: 'pl', width: 170, align: 'right',
      render: (_, r) => {
        const n = Number(r.netProfitLoss) || 0;
        return (
          <Text strong style={{ color: netProfitColor(n) }}>
            {netProfitLabel(n)}
          </Text>
        );
      }},
    { title: 'Difference', dataIndex: 'differenceAmount', width: 130, align: 'right',
      render: (v) => {
        const n = Number(v) || 0;
        return (
          <Text style={{ color: isDifferenceWithinTolerance(n) ? '#1f7a25' : '#b91c1c' }}>
            ₹ {formatRupee(Math.abs(n))}
          </Text>
        );
      }},
    { title: 'Status', dataIndex: 'status', width: 110,
      render: (v) => (
        <Tag color={CLOSING_STATUS_COLOR[v] || 'default'}>
          {CLOSING_STATUS_LABEL[v] || v}
        </Tag>
      )},
    { title: 'Closing Voucher', dataIndex: 'closingVoucherNo', width: 150,
      render: (v) => v ? <code style={{ fontSize: 11 }}>{v}</code>
                       : <Text type="secondary">—</Text> },
    { title: 'Actions', key: 'actions', width: 130, align: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Open"><Button size="small" icon={<EyeOutlined />}
            onClick={() => openDetail(r)} /></Tooltip>
          <Tooltip title="Delete"><Button size="small" icon={<DeleteOutlined />}
            danger disabled={!canDeleteClosing(r.status)}
            onClick={() => onRemove(r)} /></Tooltip>
        </Space>
      )}
  ]), [openDetail, onRemove]);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: 4 }}>
          <Title level={4} style={{ margin: 0 }}>Year-End Closing</Title>
          <Space>
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchAll}
              loading={loading}>Refresh</Button>
            <Button size="small" type="primary" icon={<PlusOutlined />}
              onClick={() => setModalOpen(true)}>New Closing</Button>
          </Space>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
          Close financial years, transfer profit/loss and create opening balances.
        </div>

        {loadError ? (
          <Alert type="error" showIcon style={{ marginBottom: 10 }}
            message="Could not load year-end closings"
            description={friendlyAccountsErrorMessage(loadError, 'year_end_closing')}
            action={<Button size="small" onClick={fetchAll}>Retry</Button>} />
        ) : null}

        <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
          <Space wrap>
            <Space>
              <Text>Status:</Text>
              <Select size="small" value={statusFilter}
                onChange={setStatusFilter} style={{ width: 150 }}
                options={[
                  { value: '',           label: 'All statuses' },
                  { value: 'draft',      label: 'Draft' },
                  { value: 'previewed',  label: 'Previewed' },
                  { value: 'closed',     label: 'Closed' },
                  { value: 'reopened',   label: 'Reopened' },
                  { value: 'cancelled',  label: 'Cancelled' }
                ]} />
            </Space>
          </Space>
        </Card>

        <Card size="small">
          <Table
            size="small" rowKey="id" loading={loading}
            columns={columns} dataSource={rows}
            pagination={{ pageSize: 25, size: 'small' }}
            locale={{ emptyText:
              <Empty description={loading ? 'Loading…'
                : friendlyAccountsEmptyMessage('year_end_closing')} /> }}
          />
        </Card>

        <NewClosingModal open={modalOpen} onClose={() => setModalOpen(false)}
          onCreated={onCreated} />

        <PreviewDrawer open={drawerOpen} closing={active}
          onClose={() => setDrawerOpen(false)}
          onRefresh={refreshActive}
          onPostDone={onPostDone} />

        {ApprovalMfaModalSlot}
      </div>
    </ErpMasterShell>
  );
}
